import React from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook, Phone, Mail, MapPin, Clock } from "lucide-react";
import { useContactInfo, getPrimaryContact, getContactByType } from "../hooks/useContactInfo";

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const { data: contactInfo } = useContactInfo();

  const address = getPrimaryContact(contactInfo, 'ADDRESS');
  const phone = getPrimaryContact(contactInfo, 'PHONE');
  const email = getPrimaryContact(contactInfo, 'EMAIL');
  const hours = getContactByType(contactInfo, 'HOURS');
  const socials = getContactByType(contactInfo, 'SOCIAL_MEDIA');

  return (
    <footer className="bg-lounge-charcoal text-lounge-light pt-12 pb-6">
      <div className="container-custom">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {/* Logo & About */}
          <div>
            <Link to="/" className="inline-block mb-4">
              <img
                src="/assets/Logo-removebg-preview.png"
                alt="Seven8Nine Lounge Logo"
                className="h-18 w-auto object-contain"
                style={{ maxWidth: 240 }}
              />
            </Link>
            <p className="my-4 text-lounge-light/80">
              Experience the finest hookah, food, and drinks in a luxurious ambiance at Winnipeg's premier hookah lounge.
            </p>
            <div className="flex space-x-4 mt-6">
              {/* Socials from DB if available, else fallback */}
              {socials.length > 0 ? (
                socials.map((social) => (
                  <a
                    key={social.id}
                    href={social.value}
                    className="text-lounge-light hover:text-lounge-gold transition-colors"
                    aria-label={social.label || social.type}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {social.label?.toLowerCase().includes('instagram') ? (
                      <Instagram size={20} />
                    ) : social.label?.toLowerCase().includes('facebook') ? (
                      <Facebook size={20} />
                    ) : null}
                  </a>
                ))
              ) : (
                <>
                  <a href="https://instagram.com" className="text-lounge-light hover:text-lounge-gold transition-colors" aria-label="Instagram">
                    <Instagram size={20} />
                  </a>
                  <a href="https://facebook.com" className="text-lounge-light hover:text-lounge-gold transition-colors" aria-label="Facebook">
                    <Facebook size={20} />
                  </a>
                </>
              )}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-xl font-display font-semibold text-lounge-gold mb-4">Quick Links</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/" className="text-lounge-light/80 hover:text-lounge-gold transition-colors">Home</Link>
              </li>
              <li>
                <Link to="/about" className="text-lounge-light/80 hover:text-lounge-gold transition-colors">About Us</Link>
              </li>
              <li>
                <Link to="/menu" className="text-lounge-light/80 hover:text-lounge-gold transition-colors">Menu</Link>
              </li>
              <li>
                <Link to="/events" className="text-lounge-light/80 hover:text-lounge-gold transition-colors">Events</Link>
              </li>
              {/* <li>
                <Link to="/gallery" className="text-lounge-light/80 hover:text-lounge-gold transition-colors">Gallery</Link>
              </li> */}
              <li>
                <Link to="/contact" className="text-lounge-light/80 hover:text-lounge-gold transition-colors">Contact</Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-xl font-display font-semibold text-lounge-gold mb-4">Contact Us</h3>
            <ul className="space-y-3">
              <li className="flex items-start">
                <MapPin size={18} className="text-lounge-gold mr-2 mt-1 flex-shrink-0" />
                <span className="text-lounge-light/80">{address?.value || "123 Main Street, Winnipeg, MB R3C 1A2"}</span>
              </li>
              <li className="flex items-center">
                <Phone size={18} className="text-lounge-gold mr-2 flex-shrink-0" />
                {phone?.value ? (
                  <a href={`tel:${phone.value}`} className="text-lounge-light/80 hover:text-lounge-gold transition-colors">{phone.value}</a>
                ) : (
                  <span className="text-lounge-light/80">(204) 555-1234</span>
                )}
              </li>
              <li className="flex items-center">
                <Mail size={18} className="text-lounge-gold mr-2 flex-shrink-0" />
                {email?.value ? (
                  <a href={`mailto:${email.value}`} className="text-lounge-light/80 hover:text-lounge-gold transition-colors">{email.value}</a>
                ) : (
                  <span className="text-lounge-light/80">info@seven8nine.com</span>
                )}
              </li>
            </ul>
          </div>

          {/* Hours */}
          <div>
            <h3 className="text-xl font-display font-semibold text-lounge-gold mb-4">Opening Hours</h3>
            <ul className="space-y-2">
              {hours.length > 0 ? (
                hours.map((h) => (
                  <li key={h.id} className="flex justify-between text-lounge-light/80">
                    <span>{h.label}</span>
                    <span>{h.value}</span>
                  </li>
                ))
              ) : (
                <>
                  <li className="flex justify-between text-lounge-light/80">
                    <span>Monday - Thursday</span>
                    <span>4:00 PM - 12:00 AM</span>
                  </li>
                  <li className="flex justify-between text-lounge-light/80">
                    <span>Friday - Saturday</span>
                    <span>4:00 PM - 2:00 AM</span>
                  </li>
                  <li className="flex justify-between text-lounge-light/80">
                    <span>Sunday</span>
                    <span>4:00 PM - 12:00 AM</span>
                  </li>
                </>
              )}
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="pt-6 border-t border-lounge-light/10 text-center text-lounge-light/60">
          <p>&copy; {currentYear} Seven8Nine Hookah Lounge. All rights reserved.</p>
          <p className="mt-2 text-sm">
            <span className="inline-block font-extrabold font-mono bg-gradient-to-r from-yellow-400 via-yellow-300 to-yellow-500 bg-[length:200%_100%] bg-clip-text text-transparent animate-shine">
              Website by <a href="mailto:makadiaviki@gmail.com" className="hover:underline hover:decoration-yellow-400 decoration-2 underline-offset-2 transition-all" target="_blank" rel="noopener noreferrer">Viki Makadia</a>
            </span>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
